import React from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';

const API_BASE =
  process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type Status = 'idle' | 'loading' | 'success' | 'error';

function VerifyEmailPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { consumeAuthResult } = useAuth();
  const [status, setStatus] = React.useState<Status>('idle');
  const [message, setMessage] = React.useState<string>('');
  const [redirecting, setRedirecting] = React.useState(false);

  React.useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setMessage(t('auth.verify_email_missing_token'));
      return;
    }

    setStatus('loading');
    fetch(`${API_BASE}/auth/verify-email?token=${encodeURIComponent(token)}`)
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(
            (body as { message?: string }).message
              || t('auth.verify_email_error'),
          );
        }
        return res.json();
      })
      .then(async (data) => {
        const payload = data as {
          accessToken?: string;
          refreshToken?: string;
          type?: string;
          email?: string;
        };
        if (payload.accessToken) {
          await consumeAuthResult({
            accessToken: payload.accessToken,
            refreshToken: payload.refreshToken ?? null,
            type: payload.type ?? null,
            email: payload.email ?? null,
          });
        }
        setStatus('success');
        setMessage(t('auth.verify_email_success'));
        setRedirecting(true);
        window.setTimeout(() => {
          const userType = payload.type || 'CUSTOMER';
          if (userType === 'CUSTOMER') {
            navigate('/account', { replace: true });
            return;
          }
          navigate('/workspace', { replace: true });
        }, 2500);
      })
      .catch((err) => {
        setStatus('error');
        setMessage(err.message || t('auth.verify_email_error'));
      });
  }, [consumeAuthResult, navigate, searchParams, t]);

  const handleBackToSignIn = () => {
    navigate('/');
  };

  return (
    <div className="gradient-bg min-h-screen text-slate-900">
      <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-500 text-xl font-semibold text-white shadow-soft">
            Z
          </div>
          <div>
            <div className="text-base font-semibold tracking-tight">
              Lewouhmart
            </div>
            <p className="text-xs text-slate-500">
              {t('layout.app_tagline')}
            </p>
          </div>
        </div>

        <div className="glass-panel w-full max-w-md p-6 sm:p-8">
          {status === 'loading' && (
            <p className="text-sm text-slate-600">
              {t('auth.verify_email_loading')}
            </p>
          )}
          {status !== 'loading' && (
            <>
              <h1 className="mb-2 text-lg font-semibold tracking-tight text-slate-900 sm:text-xl">
                {t('auth.verify_email_heading')}
              </h1>
              <p
                className={`mb-4 text-xs sm:text-sm ${
                  status === 'success'
                    ? 'text-emerald-600'
                    : status === 'error'
                      ? 'text-red-500'
                      : 'text-slate-600'
                }`}
              >
                {message}
              </p>
              {status === 'success' && redirecting ? (
                <p className="mb-4 text-xs text-slate-500 sm:text-sm">
                  Redirecting and signing you in...
                </p>
              ) : null}
              <button
                type="button"
                onClick={handleBackToSignIn}
                className="mt-2 inline-flex w-full items-center justify-center rounded-lg bg-brand-500 px-4 py-2.5 text-sm font-semibold text-white shadow-soft transition hover:bg-brand-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/80"
              >
                {t('auth.verify_email_back_to_signin')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default VerifyEmailPage;

